import { Box, Button, Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { IssueListButton } from "components/Forms/IssueListButton";
import { PrefillWasteFieldsButton } from "components/Forms/PrefillWasteFieldsButton";
import {
  Issue,
  ProgramArea,
  SubmissionStepStatus,
  SubmissionStepType,
} from "generated-graphql/graphql";
import { transform } from "hooks/transform/transformFacility";
import { useStepperNavigation } from "hooks/useStepperNavigation";
import { useTouchSubmissionMutation } from "hooks/useTouchSubmissionMutation";
import { useValidatingForm } from "hooks/useValidatingForm";
import { useWasteStepper } from "hooks/useWasteStepper";
import { useCallback, useMemo } from "react";
import { FormProvider } from "react-hook-form";
import { useFacilityInputValidation } from "routes/Customer/Chemicals/Report/validationHooks/useFacilityInputValidation";
import { FacilityDetailsInput } from "routes/Customer/Facility/FacilityProfile";
import { FacilityStateFieldsForm } from "routes/Customer/Facility/FacilityStateFieldsForm";
import {
  useFacility,
  useStateInformationIssues,
  useUpdateFacilityProfile,
} from "routes/Customer/Facility/useFacility";
import { hasCriticalIssues } from "util/forms";

import { StepHeader } from "../StepHeader";

/**
 * State Information stepper step — an inline editable copy of the facility
 * profile's state-fields form, scoped to the RCRA (Waste) program area
 * (intentionally NOT the shared `EditStateInformationDialog`, so the stepper
 * owns its own on-page layout and the merged "Save & continue" action). Saving
 * persists the waste state fields, marks the `StateFields` step complete, and
 * advances; the AL "Pre-fill from waste data" button sits in the footer.
 *
 * Frozen submissions never reach this editor (they route to the read-only
 * snapshot panel); read-only customers (`!canEdit`) see the form display-only
 * with no save action. The backend only includes this step for facilities whose
 * jurisdiction has RCRA state fields, so there are always waste fields to show.
 */
export function StateFieldsStep({
  submissionId,
  facilityId,
}: {
  submissionId: string;
  facilityId: string;
}) {
  const alerts = useAlerts();
  const { canEdit } = useWasteStepper();
  const isDisplayOnly = !canEdit;

  const { data } = useFacility(facilityId);
  const facility = data?.facility;

  const [updateFacility, { loading: saving }] = useUpdateFacilityProfile();
  const { touchStep, loading: touching } = useTouchSubmissionMutation(submissionId);
  const { goToNextStep } = useStepperNavigation();

  const allStateIssues = useStateInformationIssues(facilityId);

  // Scope everything (default values, validation, persisted issues) to the RCRA
  // (Waste) state fields — the stepper only edits the waste program area.
  const wasteFieldKeys = useMemo(
    () =>
      new Set(
        (facility?.stateFields ?? [])
          .filter((field) => field.type === ProgramArea.Rcra)
          .map((field) => field.key)
      ),
    [facility?.stateFields]
  );
  const persistedIssues = useMemo(
    () => allStateIssues.filter((issue) => !!issue.key && wasteFieldKeys.has(issue.key)),
    [allStateIssues, wasteFieldKeys]
  );

  const defaultValues = useMemo<FacilityDetailsInput>(
    () => ({
      name: facility?.name ?? "",
      state: facility?.state ?? null,
      streetAddress1: facility?.streetAddress1 ?? null,
      streetAddress2: facility?.streetAddress2 ?? null,
      city: facility?.city ?? null,
      zip: facility?.zip ?? null,
      country: facility?.country ?? null,
      county: facility?.county ?? null,
      latitude: facility?.latitude ?? null,
      longitude: facility?.longitude ?? null,
      phone: facility?.phone ?? null,
      phone24Hour: facility?.phone24Hour ?? null,
      stateFields: (facility?.stateFields ?? [])
        .filter((field) => field.type === ProgramArea.Rcra)
        .map((field) => ({ ...field, value: field.value ?? null })),
    }),
    [facility]
  );

  const validate = useFacilityInputValidation({
    facilityId: facility?.id ?? "",
    reportId: undefined,
    pick: ["stateFields"],
  });
  const validator = useCallback(
    async (input: FacilityDetailsInput): Promise<Issue[]> => {
      const result = await validate(input);
      return result.filter((issue) => !!issue.key && wasteFieldKeys.has(issue.key));
    },
    [validate, wasteFieldKeys]
  );

  const form = useValidatingForm<FacilityDetailsInput>(
    defaultValues,
    persistedIssues,
    validator,
    undefined,
    { isDisplayOnly }
  );
  const { handleSubmit, issues, watch } = form;
  const stateFields = watch("stateFields");

  const jurisdictions = useMemo(
    () =>
      [facility?.state, facility?.lepc?.id, facility?.fireDepartment?.id].filter(
        Boolean
      ) as string[],
    [facility?.state, facility?.lepc?.id, facility?.fireDepartment?.id]
  );

  const onSubmit = useCallback(
    async (formData: FacilityDetailsInput) => {
      if (!facility) return;
      const input = transform(formData);
      try {
        await updateFacility({
          variables: { id: facility.id, facility: { stateFields: input.stateFields } },
        });
        await touchStep(SubmissionStepType.StateFields, SubmissionStepStatus.Completed);
        alerts.success("State information saved.");
        goToNextStep(SubmissionStepType.StateFields);
      } catch {
        alerts.error("Error saving state information.");
      }
    },
    [facility, updateFacility, touchStep, alerts, goToNextStep]
  );

  return (
    <Stack spacing={2}>
      <StepHeader title="State Information" />
      <Typography variant="body2" color="text.secondary">
        Some states require additional information to be included on the submission. Confirm or
        update the information for your facility. Many custom state fields are optional or can be
        calculated based on other data you have already entered, so Encamp does not display them
        here.
      </Typography>
      <FormProvider {...form}>
        <form>
          <FacilityStateFieldsForm
            jurisdictions={jurisdictions}
            context="facility"
            fields={stateFields ?? []}
            namePrefix="stateFields"
            programArea={ProgramArea.Rcra}
            readOnly={isDisplayOnly}
            flush
          />
          {canEdit && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 3 }}>
              <PrefillWasteFieldsButton
                fields={stateFields ?? []}
                namePrefix="stateFields"
                facilityId={facility?.id}
              />
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: "auto" }}>
                <IssueListButton issues={issues} />
                <Button
                  variant="contained"
                  loading={saving || touching}
                  disabled={hasCriticalIssues(issues)}
                  onClick={() => handleSubmit(onSubmit)()}
                >
                  Save & continue
                </Button>
              </Box>
            </Box>
          )}
        </form>
      </FormProvider>
    </Stack>
  );
}
